import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { 
  Package, Plus, Search, X, Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';

const statusBadges = {
  'Active': 'badge-success',
  'Inactive': 'bg-gray-50 text-gray-500 border-gray-200'
};

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    physical_address: '',
    location: '',
    products_supplied: '',
    status: 'Active',
    notes: ''
  });

  // Map from the schema column name to what the frontend uses for display
  const displayFields = {
    company_name: 'name',
    physical_address: 'address'
  };

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await api.get('/suppliers');
      setSuppliers(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load suppliers');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const resetForm = () => {
    setFormData({
      company_name: '', contact_person: '', email: '',
      phone: '', physical_address: '', location: '',
      products_supplied: '', status: 'Active', notes: ''
    });
    setEditingSupplier(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.id}`, formData);
        toast.success('Supplier updated successfully');
      } else {
        await api.post('/suppliers', formData);
        toast.success('Supplier added successfully');
      }
      setShowModal(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      console.error('Failed to save supplier:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to save supplier');
    }
    setSubmitting(false);
  };

  const openEditModal = (supplier) => {
    setFormData({
      company_name: supplier.company_name || '',
      contact_person: supplier.contact_person || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      physical_address: supplier.physical_address || '',
      location: supplier.location || '',
      products_supplied: supplier.products_supplied || '',
      status: supplier.status || 'Active',
      notes: supplier.notes || ''
    });
    setEditingSupplier(supplier);
    setShowModal(true);
  };

  const filteredSuppliers = suppliers.filter(s =>
    (s.company_name || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.contact_person || '')?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-brand-blue-100 rounded-lg">
            <Package size={24} className="text-brand-blue" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-dark">Suppliers</h1>
            <p className="text-sm text-neutral-gray">Manage supplier information</p>
          </div>
        </div>

        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={18} />
          <span>Add Supplier</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-gray" />
        <input
          type="text"
          placeholder="Search suppliers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
        />
      </div>

      {/* Suppliers List */}
      {filteredSuppliers.length === 0 ? (
        <div className="text-center py-12">
          <Package size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-neutral-gray font-medium">No suppliers found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-neutral-medium overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-light">
                  <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Supplier</th>
                  <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Contact</th>
                  <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Email</th>
                  <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Status</th>
                  <th className="text-right text-sm font-medium text-neutral-gray px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.map(supplier => (
                  <tr key={supplier.id} className="border-t border-neutral-medium hover:bg-neutral-light transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-neutral-dark">{supplier.company_name}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-gray">{supplier.contact_person || '-'}</td>
                    <td className="px-4 py-3 text-sm text-neutral-gray">{supplier.email || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusBadges[supplier.status] || 'bg-gray-100'}`}>
                        {supplier.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openEditModal(supplier)}
                        className="text-brand-blue hover:text-brand-blue-700 text-sm font-medium"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-neutral-medium flex items-center justify-between">
              <h2 className="text-xl font-semibold text-neutral-dark">
                {editingSupplier ? 'Edit Supplier' : 'Add Supplier'}
              </h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="p-1 hover:bg-neutral-light rounded-lg">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Name <span className="text-brand-red">*</span></label>
                <input type="text" value={formData.company_name} onChange={(e) => setFormData(p => ({...p, company_name: e.target.value}))} required
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Contact Person</label>
                  <input type="text" value={formData.contact_person} onChange={(e) => setFormData(p => ({...p, contact_person: e.target.value}))}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                  <input type="text" value={formData.phone} onChange={(e) => setFormData(p => ({...p, phone: e.target.value}))}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData(p => ({...p, email: e.target.value}))}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Physical Address</label>
                  <input type="text" value={formData.physical_address} onChange={(e) => setFormData(p => ({...p, physical_address: e.target.value}))}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Location</label>
                  <input type="text" value={formData.location} onChange={(e) => setFormData(p => ({...p, location: e.target.value}))}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Products Supplied</label>
                <input type="text" value={formData.products_supplied} onChange={(e) => setFormData(p => ({...p, products_supplied: e.target.value}))}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                <select value={formData.status} onChange={(e) => setFormData(p => ({...p, status: e.target.value}))}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white">
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData(p => ({...p, notes: e.target.value}))} rows={2}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-medium">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-5 py-2.5 border border-neutral-medium rounded-xl text-sm font-medium text-gray-700 hover:bg-neutral-light">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
                  {submitting ? <span className="flex items-center space-x-2"><Loader2 size={16} className="animate-spin" /><span>Saving...</span></span>
                    : editingSupplier ? 'Update Supplier' : 'Add Supplier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;